<template>
    <popup v-model="isActive" @closed="handleCancel" zoom-transition highest-order>
        <card width="480" max-width="85%" class="popup-alert p-16 mb-0" rounded-x-large>
            <div v-if="displayIcon" class="flex justify-center items-center mb-4 mt-20">
                <lazy-icon xx-large :color="type" :icon="displayIcon" prominent/>
            </div>
            <card-title center x-large>{{ title }}</card-title>
            <card-text center class="text-sub">
                <div v-html="text"/>

                <div v-if="inputs && inputs.length" class="mt-16">
                    <lazy-dynamic-form-input v-for="(input, inputKey) in inputs" :key="`${inputKey}-${input.name}`" :type="input.type"
                                        :name="input.name"
                                        :label="input.label ?? null"
                                        :placeholder="input.placeholder ?? null"
                                        :help="input.help ?? null"
                                        :clearable="input.clearable ?? false"
                                        :rules="input.rules ?? null"
										:input-props="input.inputProps ?? {}"
										:options="input.options ?? []"
                                        v-model:valid="inputValidationStates[inputKey]"
                                        v-model="inputData[input.name]"/>
                </div>
            </card-text>

            <lazy-card-buttons v-if="showConfirmButton || showCancelButton" center stretch :break-column="buttonsBreakColumn">
                <lazy-btn v-if="showCancelButton" ref="cancelButton" large @click="handleCancel">
                    {{ displayCancelText }}
                </lazy-btn>
                <lazy-btn v-if="showConfirmButton" ref="confirmButton" :color="type === 'info' ? 'success' : type" large @click="handleConfirm" :disabled="!hasValidInputs">
                    {{ displayConfirmText }}
                </lazy-btn>
            </lazy-card-buttons>
        </card>
    </popup>
</template>

<script>
import AutoToggleable from "../../mixins/AutoToggleable";
	import {mdiCheck, mdiClose, mdiExclamation, mdiInformationVariant} from "@mdi/js";

    export default {
        name: "PopupAlert",

        mixins: [AutoToggleable],

		emits: ['confirm', 'cancel', 'hide'],

        props: {

            type: {
                type: String,
                default: 'info',
                validator: function (value) {
                    // The value must match one of these strings
                    return ['success', 'warning', 'error', 'info'].indexOf(value) !== -1
                }
            },

            title: {
                type: String,
                default: null
            },

            text: {
                type: String,
                default: null
            },

            icon: {
                type: [String, Boolean],
                default: null
            },

            confirm: {
                type: Boolean,
                default: false
            },

            confirmText: {
                type: String,
                default: null
            },

            cancel: {
                type: Boolean,
                default: false
            },

            cancelText: {
                type: String,
                default: null
            },

            inputs: {
                type: Array,
                default: null
            },
        },

        data() {
            return {
                inputData: {},
                inputValidationStates: {}
            }
        },

        computed: {
            displayIcon() {
                if (this.icon === false) {
                    return null;
                }
                if (this.icon) {
                    return this.icon;
                }
                switch (this.type) {
                    case 'success':
                        return mdiCheck;
                    case 'warning':
                        return mdiExclamation;
                    case 'error':
                        return mdiClose;
                }

                return mdiInformationVariant;
            },

            displayConfirmText() {
                return this.confirmText ? this.confirmText : this.$t('confirm.default.accept')
            },

            displayCancelText() {
              return this.cancelText ? this.cancelText : this.$t('confirm.default.cancel')
            },

            showConfirmButton() {
                return this.confirm || this.confirmText;
            },

            showCancelButton() {
                return this.cancel || this.cancelText;
            },

            autoToggleEnabled() {
                return this.duration && this.duration > 0 && !this.showConfirmButton;
            },

            buttonsBreakColumn() {
                // Break the buttons when the button text is too long to display
                if (!this.$breakpoint.xsOnly || !this.showConfirmButton || !this.showCancelButton) {
                    return null;
                }

                return (this.displayConfirmText.length + this.displayCancelText.length) > 25 ? 'xs' : null;
            },

            hasValidInputs() {
                return Object.values(this.inputValidationStates).every(value => !!value);
            }
        },

        watch: {
            inputs: {
                handler: function(inputs) {
                    const inputData = {};
                    if (inputs && inputs.length) {
                        inputs.forEach(input => {
                            inputData[input.name] = input.value;
                        })
                    }
                    this.inputValidationStates = {};
                    this.inputData = inputData;
                },
                immediate: true
            },

            isActive: {
                handler: function(isActive) {
                    setTimeout(() => {
                        if (isActive && this.$refs.confirmButton) {
                            this.$refs.confirmButton.$el.focus();
                        }
                    }, 0);

                    if (isActive) {
                        document.addEventListener('keydown', this.handleControlEvent);
                    } else {
                        document.removeEventListener('keydown', this.handleControlEvent);
                    }
                }
			},
        },

        methods: {
            handleConfirm() {
				// We must wait for the popup to be fully hidden before emitting the events
				// as often the popup close listeners are bound to page redirects
				// which might cause non-reactive redirected pages due the popup to be not fully destroyed before redirect
				this.hide().then(() => {
					this.$emit('hide');
					this.$emit('confirm', this.inputData);
				});
            },

            handleCancel() {
				// We must wait for the popup to be fully hidden before emitting the events
				// as often the popup close listeners are bound to page redirects
				// which might cause non-reactive redirected pages due the popup to be not fully destroyed before redirect
				this.hide().then(() => {
					this.$emit('hide');
					this.$emit('cancel');
				});
            },

			async hide() {
				this.isActive = false;
				return new Promise(resolve => {
					setTimeout(() => {
					    resolve();
					}, 400); // This must be at least the duration of the popup close transition
				});
			},

            handleControlEvent(event) {
                if (!event || !event.key) {
                    return;
                }

                const key = event.key.toLowerCase();
                const activeElement = document.activeElement;

                if (key === 'arrowleft' && activeElement && this.$refs.confirmButton && this.$refs.confirmButton.$el === activeElement) {
                    if (this.$refs.cancelButton) {
                        this.$refs.cancelButton.$el.focus();
                    }
                } else if (key === 'arrowright' && activeElement && this.$refs.cancelButton && this.$refs.cancelButton.$el === activeElement) {
                    if (this.$refs.confirmButton) {
                        this.$refs.confirmButton.$el.focus();
                    }
                }
            }
        }
    }
</script>